<template>
  <div class="video-component" v-click-outside="showModalLeaveVideo" id="video-component">
    <div class="video-header">
      <span>ビデオ通話</span>
      <span>
        {{ `${currentChannelUser.family_name || ''} ${currentChannelUser.given_name || ''}`.trim() || '-' }}
      </span>
    </div>

    <img v-if="userMicOff" :src="micUserIcon" class="mic-icon-user"  alt="Micro"/>
    <div class="video-container">
      <div class="video-content" id="video-content"></div>
      <div class="video-user">
        <div class="video-content-user" id="video-content-user"></div>
      </div>
    </div>
    <div class="video-footer">
      <div class="video-item">
        <img :src="camIcon" @click="camToggle()"  alt="Camera"/>
      </div>
      <div class="video-item">
        <button
          v-if="isCall"
          class="video-btn video-btn-call"
          @click="leaveCalling"
        >
          退出する
        </button>
        <button
          v-else
          class="video-btn video-btn-end"
          id="join"
          @click="joinCalling"
        >
          開始する
        </button>
      </div>
      <div class="video-item">
        <img :src="micIcon" @click="micToggle()"  alt="Micro"/>
      </div>
      <div class="video-item" v-if="isCall && backgroundEnabled">
        <button class="video-btn video-btn-background" @click="toggleBackgroundPopup">
          背景設定
        </button>
      </div>
    </div>
    <div class="background-popup" v-if="showBackgroundPopup && isCall && backgroundEnabled">
      <BackgroundGallery
        :backgrounds="backgrounds"
        :currentId="currentBackgroundId"
        :allowDelete="false"
        @select="selectBackgroundDuringCall"
      />
    </div>
  </div>
</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator';
import AgoraRTC from 'agora-rtc-sdk-ng';
import Toast from '@common/helpers/toast';
import ApiHelper from 'api-helper';
import BackgroundGallery from '@/components/common/BackgroundGallery.vue';
import { backgroundErrorMessage } from '@/common/helpers/background-error';
import { findBackgroundById } from '@/common/helpers/background';
import CamOn from '@/assets/img/camera.svg';
import CamOff from '@/assets/img/camera-lock.svg';
import MicOn from '@/assets/img/mic.svg';
import MicOff from '@/assets/img/mic-lock.svg';

const BACKGROUND_IMAGE_TIMEOUT_MS = 8000;

// The extension bundle is ~1.8MB with the wasm inlined. Loading it lazily keeps
// it out of the vendor chunk that every admin role downloads on boot, since
// only doctors can start a video call. Resolved once and reused.
let virtualBackgroundExtension = null;

async function loadVirtualBackgroundExtension() {
  if (!virtualBackgroundExtension) {
    const module = await import('agora-extension-virtual-background');
    const VirtualBackgroundExtension = module.default || module;
    virtualBackgroundExtension = new VirtualBackgroundExtension();
    AgoraRTC.registerExtensions([virtualBackgroundExtension]);
  }

  return virtualBackgroundExtension;
}

@Component({
  components: {
    BackgroundGallery,
  },
})
export default class VideoComponent extends Vue {
  @Prop() currentChannel;
  @Prop() currentChannelUser;
  @Prop() setVideo;
  @Prop() showModalLeaveVideo;

  agoraTokenData = {};
  hasMic = true;
  hasCamera = true;
  isCall = false;
  hasUser = false;
  userMicOff = false;
  backgrounds = [];
  showBackgroundPopup = false;
  currentBackgroundId = null;
  backgroundEnabled = false;
  backgroundsPromise = null;
  isDestroyed = false;

  rtc = {
    localAudioTrack: null,
    localVideoTrack: null,
    client: null,
    processor: null,
    localPlayerContainer: null,
    connectionStatus: null,
  };

  options = {
    // Pass your App ID here.
    appId: 'Your App ID',
    // Set the channel name.
    channel: 'test',
    // Pass your temp token here.
    token: 'Your temp token',
    // Set the user ID.
    uid: 123456,
  };

  created() {
    // No.77: another tab/device the doctor has open may toggle
    // ビデオ通話の背景設定 or change the selected background while this one is
    // mid-call. Refetch and re-apply instead of showing a stale list/selection
    // until the doctor happens to reopen the popup.
    this.$on('UpdateBackgroundSetting', this.onBackgroundSettingUpdated);
  }

  mounted() {
    this.getVideoToken();
    this.backgroundsPromise = this.fetchBackgrounds();
    // this.startBasicCall();
  }

  get currentUser() {
    return this.$store.state.auth.currentUser;
  }

  micToggle() {
    this.hasMic = !this.hasMic;
    this.rtc.localAudioTrack.setEnabled(this.hasMic);
  }

  camToggle() {
    this.hasCamera = !this.hasCamera;
    this.rtc.localVideoTrack.setEnabled(this.hasCamera);
  }

  // (Re)plays the local video track into `container`. Agora's play() always
  // creates a NEW <video> element inside the container instead of reusing the
  // existing one — calling it again while the track is already playing (e.g.
  // this reconnect-restore handler firing more than once) would otherwise
  // leave a stale blank <video> element behind. stop() first so play() always
  // starts from a clean container.
  restartLocalVideoTrack(container) {
    if (this.rtc.localVideoTrack.isPlaying) {
      this.rtc.localVideoTrack.stop();
    }
    this.rtc.localVideoTrack.play(container);
  }

  get camIcon() {
    return this.hasCamera ? CamOn : CamOff;
  }

  get micIcon() {
    return this.hasMic ? MicOn : MicOff;
  }

  get micUserIcon() {
    return this.userMicOff ? MicOff : MicOn;
  }

  async getVideoToken() {
    await ApiHelper.getApi('ChannelApi')
      .agoraToken(this.$route.params.channelId, {
        with: 'users,latest_message,users.latestReservation',
      })
      .then((response) => {
        this.agoraTokenData = response.data;
        this.options = {
          appId: response.data.app_id,
          channel: response.data.channel_uuid,
          token: response.data.token,
          uid: Number(response.data.uid),
        };
        this.startBasicCall();
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(error.response.data.message);
        }
      })
      .finally(() => {
        // no-op
      });
  }

  async fetchBackgrounds() {
    try {
      // getBackgroundSetting, not getDetail: `GET /${id}` sits behind
      // role:clinic_admin server-side and would 403 a doctor reading their
      // own record during a call.
      const response = await ApiHelper.getApi('DoctorApi').getBackgroundSetting(this.currentUser.id);
      this.backgroundEnabled = !!response.background_enabled;
      this.currentBackgroundId = response.background_id || null;

      const backgroundsResponse = await ApiHelper.getApi('DoctorApi').getBackgrounds(this.currentUser.id);
      this.backgrounds = backgroundsResponse.data;
    } catch (error) {
      Toast.error(backgroundErrorMessage(error));
    }
  }

  async toggleBackgroundPopup() {
    const opening = !this.showBackgroundPopup;
    this.showBackgroundPopup = opening;

    // image_url is a presigned URL that expires after an hour. A console left
    // open across a morning of consultations would otherwise hold dead URLs,
    // giving broken thumbnails and a failed apply. Refresh them on open.
    if (opening) {
      this.backgroundsPromise = this.fetchBackgrounds();
      await this.backgroundsPromise;
    }
  }

  async selectBackgroundDuringCall(background) {
    const previousBackgroundId = this.currentBackgroundId;
    const applied = await this.applyBackgroundToTrack(background);
    if (!applied) {
      return;
    }
    this.currentBackgroundId = background.id;

    try {
      await ApiHelper.getApi('DoctorApi').updateBackgroundSetting(this.currentUser.id, {
        background_id: background.id,
        background_enabled: true,
      });
      this.backgroundEnabled = true;
    } catch (error) {
      // The track already shows the new image. Put the previous one back so the
      // patient never sees a background the server did not accept.
      this.currentBackgroundId = previousBackgroundId;
      const previous = findBackgroundById(this.backgrounds, previousBackgroundId);
      if (previous) {
        await this.applyBackgroundToTrack(previous);
      } else if (this.rtc.processor) {
        try {
          await this.rtc.processor.disable();
        } catch (disableError) {
          // Best-effort — nothing more we can do here; the user is already
          // informed via the Toast.error below.
        }
      }
      Toast.error(backgroundErrorMessage(error));
    }
  }

  // No.77: another tab/device changed this doctor's backgrounds. Only
  // relevant while actually on a call — refetch (fresh presigned URLs +
  // current list) and re-apply the track image if the selection or the
  // enabled flag itself changed. Never call updateBackgroundSetting here —
  // this handler only mirrors a change that already happened elsewhere, it
  // must not write it back.
  async onBackgroundSettingUpdated() {
    if (!this.isCall) {
      return;
    }

    const previousBackgroundId = this.currentBackgroundId;
    const previousBackgroundEnabled = this.backgroundEnabled;
    await this.fetchBackgrounds();

    if (this.currentBackgroundId === previousBackgroundId
      && this.backgroundEnabled === previousBackgroundEnabled) {
      return;
    }

    const nextBackground = findBackgroundById(this.backgrounds, this.currentBackgroundId);
    if (this.backgroundEnabled && nextBackground) {
      await this.applyBackgroundToTrack(nextBackground);
    } else if (this.rtc.processor) {
      // Selected background was deleted/disabled elsewhere and no longer
      // resolves — fall back to no virtual background rather than keep
      // showing a stale image.
      try {
        await this.rtc.processor.disable();
      } catch (disableError) {
        // Best-effort — nothing more we can do here.
      }
    }
  }

  async applyBackgroundToTrack(background) {
    if (!this.rtc.processor) {
      Toast.error('背景設定機能が利用できないため、背景を変更できません。');
      return false;
    }
    if (!background) {
      return false;
    }

    try {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.src = background.image_url;
      // Bounded wait: image_url is a presigned S3 URL, and a request that is
      // blackholed by a clinic firewall fires neither onload nor onerror. This
      // promise gates joinCalling(), so an unbounded wait would hang the call.
      await new Promise((resolve, reject) => {
        const timer = setTimeout(
          () => reject(new Error('background image load timed out')),
          BACKGROUND_IMAGE_TIMEOUT_MS,
        );
        image.onload = () => {
          clearTimeout(timer);
          resolve(undefined);
        };
        image.onerror = () => {
          clearTimeout(timer);
          reject(new Error('background image failed to load'));
        };
      });

      if (this.isDestroyed || !this.rtc.processor) {
        return false;
      }

      await this.rtc.processor.setOptions({ type: 'img', source: image });
      await this.rtc.processor.enable();
      return true;
    } catch (error) {
      Toast.error('背景画像の読み込みに失敗したため、背景を変更できませんでした。');
      return false;
    }
  }

  async initBackgroundProcessor() {
    let extension;
    try {
      extension = await loadVirtualBackgroundExtension();
    } catch (error) {
      Toast.error('背景設定機能の読み込みに失敗しました。');

      return;
    }

    if (!extension.checkCompatibility()) {
      Toast.error('お使いのブラウザは背景設定機能に対応していません。Chrome/Edgeの最新版をご利用ください。');
      return;
    }

    let processor = null;
    try {
      processor = extension.createProcessor();
      await processor.init();

      // The component can be torn down while init() loads its wasm; without
      // this guard the processor would be piped into a track that has already
      // been closed and would leak for the rest of the session.
      if (this.isDestroyed) {
        await processor.release();
        return;
      }

      this.rtc.processor = processor;
      this.rtc.localVideoTrack.pipe(this.rtc.processor).pipe(this.rtc.localVideoTrack.processorDestination);

      if (this.backgroundEnabled && this.currentBackgroundId) {
        const currentBackground = findBackgroundById(this.backgrounds, this.currentBackgroundId);
        await this.applyBackgroundToTrack(currentBackground);
      }
    } catch (error) {
      this.rtc.processor = null;
      // Release a processor that failed midway through init(), otherwise its
      // worker and wasm allocations stay alive until the page is reloaded.
      if (processor) {
        try {
          await processor.release();
        } catch (releaseError) {
          // Best-effort cleanup — nothing more we can do here; the user is
          // already informed via the Toast.error below.
        }
      }
      Toast.error('背景設定機能の読み込みに失敗しました。');
    }
  }

  async startBasicCall() {
    // Create an AgoraRTCClient object.
    this.rtc.client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });

    // Handle Agora connection state changes so that the local video track's
    // render pipeline is restarted after a hard network drop + auto-reconnect.
    // Without this, the local camera preview stays as a frozen/colored block
    // on the doctor's side even though the published stream is still visible
    // to the patient (it's a client-side render stall, not a publish issue).
    this.rtc.client.on('connection-state-change', async (curState) => {
      if (curState === 'RECONNECTING') {
        this.rtc.connectionStatus = 'reconnecting';
      } else if (curState === 'DISCONNECTED') {
        this.rtc.connectionStatus = 'disconnected';
      } else if (curState === 'CONNECTED') {
        const wasInterrupted = this.rtc.connectionStatus === 'reconnecting'
          || this.rtc.connectionStatus === 'disconnected';
        this.rtc.connectionStatus = null;

        if (wasInterrupted
          && this.rtc.localVideoTrack
          && this.rtc.localPlayerContainer
          && this.hasCamera) {
          try {
            this.rtc.localVideoTrack.setEnabled(true);
            this.restartLocalVideoTrack(this.rtc.localPlayerContainer);
          } catch (error) {
            // Restoring the local video track after reconnect can legitimately
            // fail (e.g. track already closed) — swallow it, a later reconnect
            // will retry.
          }
        }
      }
    });

    // Listen for the 'user-published' event, from which you can get an AgoraRTCRemoteUser object.
    this.rtc.client.on('user-published', async (user, mediaType) => {
      // Subscribe to the remote user when the SDK triggers the 'user-published' event
      await this.rtc.client.subscribe(user, mediaType);

      // If the remote user publishes a video track.
      if (mediaType === 'video') {
        // Get the RemoteVideoTrack object in the AgoraRTCRemoteUser object.
        const remoteVideoTrack = user.videoTrack;
        const videoContent = document.getElementById('video-content');
        // Reuse a stale container for the same uid in place (e.g. after a
        // network reconnect user-published fires again without an
        // intervening user-unpublished) — clearing its content, rather than
        // removing and recreating the element, avoids leaving a second blank
        // box alongside the freshly-created one.
        let remotePlayerContainer = document.getElementById(user.uid.toString());
        if (remotePlayerContainer) {
          // remoteVideoTrack is the SDK's per-uid RemoteVideoTrack instance,
          // reused across a reconnect — stop it before detaching its old
          // <video> element so it cleanly restarts into the container instead
          // of continuing to render into a node no longer in the document.
          if (remoteVideoTrack?.isPlaying) {
            remoteVideoTrack.stop();
          }
          remotePlayerContainer.innerHTML = '';
        } else {
          // Dynamically create a container in the form of a DIV element for playing the remote video track.
          remotePlayerContainer = document.createElement('div');
          // Specify the ID of the DIV container. You can use the uid of the remote user.
          remotePlayerContainer.id = user.uid.toString();
          // remotePlayerContainer.textContent = 'Remote user ' + user.uid.toString();
          remotePlayerContainer.style.width = '100%';
          remotePlayerContainer.style.height = 'calc(100vh - 12vh)';
          videoContent.append(remotePlayerContainer);
        }
        // document.body.append(remotePlayerContainer);

        // Play the remote video track.
        // Pass the DIV container and the SDK dynamically creates a player in the container for playing the remote video track.
        // remoteVideoTrack.play(remotePlayerContainer);
        remoteVideoTrack.play(remotePlayerContainer, { fit: 'contain', mirror: false });
      }

      // If the remote user publishes an audio track.
      if (mediaType === 'audio') {
        this.userMicOff = false;
        // Get the RemoteAudioTrack object in the AgoraRTCRemoteUser object.
        const remoteAudioTrack = user.audioTrack;
        // Play the remote audio track. No need to pass any DOM element.
        remoteAudioTrack.play();
      }

      // Listen for the 'user-unpublished' event
      this.rtc.client.on('user-unpublished', (user, mediaType) => {
        // Get the dynamically created DIV container.
        const remotePlayerContainer = document.getElementById(user.uid);
        // Destroy the container.

        // If the remote user publishes an audio track.
        if (mediaType === 'audio') {
          this.userMicOff = true;
        }
        if (mediaType === 'video') {
          remotePlayerContainer.remove();
        }
        });
    });
  }

  async joinCalling() {
    // document.getElementById('join').onclick = async function () {
    // Join an RTC channel.
    await this.rtc.client.join(
      this.options.appId,
      this.options.channel,
      this.options.token,
      this.options.uid,
    );
    // Create a local audio track from the audio sampled by a microphone.
    this.rtc.localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack();
    // Create a local video track from the video captured by a camera.
    this.rtc.localVideoTrack = await AgoraRTC.createCameraVideoTrack();
    // Re-fetch rather than await the stale promise captured in mounted(): this
    // component stays mounted across multiple join/leave cycles, so a setting
    // change made after mount (e.g. turning ビデオ通話の背景設定 OFF) would
    // otherwise never be picked up by a later call.
    this.backgroundsPromise = this.fetchBackgrounds();
    await this.backgroundsPromise;
    await this.initBackgroundProcessor();
    // Loading the backgrounds and the processor's wasm takes a few seconds. If
    // the doctor navigated away meanwhile, beforeDestroy has already left the
    // channel — publishing now would leak the camera into a channel we left,
    // and the player container it expects is gone from the DOM.
    if (this.isDestroyed) {
      this.releaseLocalTracks();

      return;
    }
    // Publish the local audio and video tracks to the RTC channel.
    await this.rtc.client.publish([
      this.rtc.localAudioTrack,
      this.rtc.localVideoTrack,
    ]);
    // Dynamically create a container in the form of a DIV element for playing the local video track.
    const localPlayerContainer = document.createElement('div');
    // Specify the ID of the DIV container. You can use the uid of the local user.
    localPlayerContainer.id = this.options.uid;
    // localPlayerContainer.textContent = 'Local user ' + this.options.uid;
    localPlayerContainer.style.width = '200px';
    localPlayerContainer.style.height = '200px';
    const videoContentUser = document.getElementById('video-content-user');
    videoContentUser.append(localPlayerContainer);
    this.rtc.localPlayerContainer = localPlayerContainer;

    // Play the local video track.
    // Pass the DIV container and the SDK dynamically creates a player in the container for playing the local video track.
    this.rtc.localVideoTrack.play(localPlayerContainer);
    if (!this.hasCamera) {
      await this.rtc.localVideoTrack.setEnabled(false);
    }
    if (!this.hasMic) {
      await this.rtc.localAudioTrack.setEnabled(false);
    }
    this.isCall = true;
  }

  /**
   * Tears the virtual background out of the video pipeline. Never rejects: a
   * failure here must not stop the caller from closing the camera/mic tracks
   * and leaving the channel.
   */
  async teardownBackgroundProcessor() {
    const processor = this.rtc.processor;
    if (!processor) {
      return;
    }
    this.rtc.processor = null;

    try {
      // Unpipe before releasing — releasing a processor that is still wired
      // into the track leaves the SDK pipeline referencing freed resources.
      processor.unpipe();
      if (this.rtc.localVideoTrack) {
        this.rtc.localVideoTrack.unpipe();
      }
      await processor.disable();
      await processor.release();
    } catch (error) {
      // Best-effort — never let a teardown failure block the caller from
      // closing the camera/mic tracks and leaving the channel.
    }
  }

  /** Closes the camera and mic tracks if they were created. Never throws. */
  releaseLocalTracks() {
    try {
      if (this.rtc.localAudioTrack) {
        this.rtc.localAudioTrack.close();
        this.rtc.localAudioTrack = null;
      }
      if (this.rtc.localVideoTrack) {
        this.rtc.localVideoTrack.close();
        this.rtc.localVideoTrack = null;
      }
    } catch (error) {
      // Best-effort — never let a track-close failure surface to the caller.
    }
  }

  async leaveCalling() {
    // Destroy the local audio and video tracks.
    await this.teardownBackgroundProcessor();
    this.showBackgroundPopup = false;
    this.rtc.localAudioTrack.close();
    this.rtc.localVideoTrack.close();

    // Traverse all remote users.
    this.rtc.client.remoteUsers.forEach((user) => {
      // Destroy the dynamically created DIV containers.
      const playerContainer = document.getElementById(user.uid);
      playerContainer && playerContainer.remove();
    });

    // Leave the channel.
    await this.rtc.client.leave();
    this.rtc.localPlayerContainer = null;
    this.rtc.connectionStatus = null;
    this.isCall = false;
    this.setVideo(false);
  }

  beforeDestroy() {
    this.$off('UpdateBackgroundSetting', this.onBackgroundSettingUpdated);
    this.isDestroyed = true;
    // Vue cannot await a lifecycle hook; teardownBackgroundProcessor swallows
    // its own errors so this can never surface as an unhandled rejection.
    this.teardownBackgroundProcessor();
    this.rtc.client.leave();
    this.setVideo(false);
  }
}
</script>

<style lang='scss' scoped>
.video {
  &-component {
    height: calc(100vh - 8vh);
    position: relative;
  }
  &-header {
    background-color: #000;
    padding: 10px;
    color: #fff;
    font-weight: bold;

    span {
      margin-left: 10px;
    }
  }

  &-content {
    padding-top: 10px;
    z-index: 1;
  }

  &-item {
    padding: 10px 10px 0;
    z-index: 1000;
  }

  &-footer {
    display: flex;
    justify-content: center;
    align-items: center;
    align-self: center;
    position: absolute;
    bottom: -12px;
    width: 100%;
    padding: 10px 10px 10px;
  }

  &-btn {
    color: #fff;
    font-weight: bold;
    border-radius: 6px;
    padding: 10px;
    border: unset;

    &-call {
      background: #fc1d1d;
    }
    &-end {
      background: #008505;
    }
    &-background {
      background: #555;
    }
  }

  &-container {
    position: relative;
    height: 90%;
    background-color: #D9D9D9;
  }

  &-user {
    position: absolute;
    right: 18px;
    bottom: 10px;
    width: 200px;
    height: 200px;
    z-index: 100;
    background-color: transparent;
  }
}
.mic-icon-user {
  position: absolute;
  top: 58px;
  z-index: 1000;
  height: 30px;
  left: 20px;
}
.background-popup {
  position: absolute;
  bottom: 70px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1001;
  background: #fff;
  padding: 12px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
  // The gallery itself scrolls horizontally (BackgroundGallery.vue), so the
  // popup only needs to bound its width to the viewport.
  width: #{"min(560px, 90vw)"};
  max-width: 90vw;

  @media (max-width: 767px) {
    width: #{"min(320px, 90vw)"};
  }
}
</style>
